import React from 'react';

const escapeRegexSpecialChars = (term: string): string => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const MATCH_BEGINNING_OF_LINE = '^';
const CASE_INSENSITIVE_ALL_OCCURRENCES_REGEX_MODIFIER = 'gi';

/**
 * Create regex to split up the `optionLabel` by the `inputValue`.
 */
export const getLabelSplitByInput = ({
  inputValue,
  optionLabel,
  highlightJustBeginning,
}: {
  inputValue: string;
  optionLabel: string;
  highlightJustBeginning: boolean;
}): string[][] => {
  // Trailing whitespace in inputValue is filtered out.
  const terms = inputValue.split(/\s+/).filter((lb) => lb.length > 0);
  // Trailing whitespace is filtered out.
  const labelParts = optionLabel.split(/\s+/).filter((lb) => lb.length > 0);
  const prefix = highlightJustBeginning ? MATCH_BEGINNING_OF_LINE : '';

  const parsedTerms = terms.map(escapeRegexSpecialChars).join('|');
  const regex = new RegExp(`${prefix}(${parsedTerms})`, CASE_INSENSITIVE_ALL_OCCURRENCES_REGEX_MODIFIER);

  try {
    return labelParts.map((lp) => lp.split(regex).filter((w) => w.length > 0));
  } catch {
    return labelParts.map((part) => [part]);
  }
};

/**
 * Attach information to each split label part for whether to bold.
 */
export const getFormattedSplitLabelParts = ({
  labelSplitByInput,
  inputValue,
}: {
  labelSplitByInput: string[][];
  inputValue: string;
}) => {
  const terms = inputValue.split(/\s+/);
  const lowerTerms = terms.map((t) => t.toLowerCase());

  const formattedParts = labelSplitByInput.map((wordPieces) => {
    // Array of pieces of word, with whether to bold or not.
    const singleWord = wordPieces.map((slpp) => ({ value: slpp, isBolded: lowerTerms.includes(slpp.toLowerCase()) }));

    const allWordsBold = singleWord.every((p) => p.isBolded === true);
    const allWordsNotBold = singleWord.every((p) => p.isBolded === false);

    if (allWordsBold || allWordsNotBold) {
      return { value: singleWord.map((s) => s.value).join(''), isBolded: singleWord[0].isBolded };
    }

    return singleWord;
  });
  return formattedParts;
};

const optionallyBoldContent = ({
  content,
  shouldBold,
  uniqueIdentifier,
}: {
  content: string;
  shouldBold: boolean;
  uniqueIdentifier: string;
}): string | JSX.Element => (shouldBold ? <b key={`bolded-${uniqueIdentifier}`}>{content}</b> : content);

export type FormattedPart =
  | {
      value: string;
      isBolded: boolean;
    }[]
  | {
      value: string;
      isBolded: boolean;
    };

/**
 * Combine formatted split label parts into single value.
 */
const getCombinedFormattedValue = ({
  formattedParts,
}: {
  formattedParts: FormattedPart[];
}): (string | JSX.Element)[] => {
  return formattedParts.reduce((soFar, curr, index) => {
    const value = !Array.isArray(curr)
      ? optionallyBoldContent({ content: curr.value, shouldBold: curr.isBolded, uniqueIdentifier: index.toString() })
      : curr.map((c, i) =>
          optionallyBoldContent({ content: c.value, shouldBold: c.isBolded, uniqueIdentifier: `${index}-${i}` }),
        );

    // This is how we add spacing between words.
    // CSS `white-space: pre` is used to preserve the spacing.
    const wrappedValue = <span key={index}>{value} </span>;

    return [...soFar, wrappedValue];
  }, [] as (string | JSX.Element)[]);
};

/**
 * Bolds parts of `optionLabel` that are matched to `inputValue`.
 */
export const getContent = ({
  inputValue,
  optionLabel,
  highlightJustBeginning,
}: {
  inputValue: string;
  optionLabel: string;
  highlightJustBeginning: boolean;
}): (string | JSX.Element)[] => {
  const labelSplitByInput: string[][] = getLabelSplitByInput({ inputValue, optionLabel, highlightJustBeginning });

  const formattedParts = getFormattedSplitLabelParts({ labelSplitByInput, inputValue });

  const combinedFormattedValue = getCombinedFormattedValue({ formattedParts });

  return combinedFormattedValue;
};
